/**
 * How one period of the profit-and-loss statement reads: its lines, its headline, and the order the
 * periods are shown in. A port of `PlPeriodVm` from the OpenSilver view model, kept apart from the page
 * so the wording and the arithmetic of the statement can be tested without a canvas.
 */
import type { ProfitLossFiguresDto, ProfitLossPeriodDto, ProfitLossReportDto } from './contracts.gen.js';

const MONEY = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * A loss reads as ($1,234.56) - the convention on every statement an accountant sees. The rest of the
 * app writes -$1,234.56; this one page keeps the brackets.
 */
export function accountingMoney(value: number): string {
  return value < 0 ? `($${MONEY.format(Math.abs(value))})` : `$${MONEY.format(value)}`;
}

function margin(value: number): string {
  return `${(Math.round(value * 10) / 10).toString()}%`;
}

export interface StatementLine {
  label: string;
  accrual: string;
  /** '—' where the figure has no cash reading (an issued, unpaid balance is an accrual idea). */
  cash: string;
  /** A subtotal: revenue, cost of sales, gross and net profit. */
  emphasis: boolean;
}

export function netLabel(period: ProfitLossPeriodDto): string {
  return period.accrual.netProfit < 0 ? 'Net loss' : 'Net profit';
}

export function headline(period: ProfitLossPeriodDto): string {
  return `${netLabel(period)} ${accountingMoney(period.accrual.netProfit)} accrual · ${accountingMoney(period.cash.netProfit)} cash`;
}

/** The statement, top to bottom, exactly as the XAML template and the PDF lay it out. */
export function statementLines(p: ProfitLossPeriodDto): StatementLine[] {
  const line = (label: string, pick: (f: ProfitLossFiguresDto) => number, emphasis = false): StatementLine => ({
    label,
    accrual: accountingMoney(pick(p.accrual)),
    cash: accountingMoney(pick(p.cash)),
    emphasis
  });
  return [
    line('Revenue — labor', f => f.laborRevenue),
    line('Revenue — parts', f => f.partsRevenue),
    line('Revenue — other', f => f.otherRevenue),
    line('Revenue (ex tax)', f => f.revenue, true),
    line('Cost of sales — parts', f => f.partsCost),
    line('Cost of sales — non-billable parts', f => f.absorbedCost),
    line('Cost of sales — direct expenses', f => f.directExpenses),
    line('Cost of sales', f => f.costOfSales, true),
    line(`Gross profit (${margin(p.accrual.grossMarginPercent)} / ${margin(p.cash.grossMarginPercent)})`, f => f.grossProfit, true),
    line('Operating expenses', f => f.operatingExpenses),
    line(`${netLabel(p)} (${margin(p.accrual.netMarginPercent)} / ${margin(p.cash.netMarginPercent)})`, f => f.netProfit, true),
    line('Sales tax billed (not revenue)', f => f.taxCollected),
    { label: 'Issued and unpaid', accrual: accountingMoney(p.accrual.outstanding), cash: '—', emphasis: false },
    { label: 'Parts bought, not yet invoiced', accrual: accountingMoney(p.accrual.deferredPartsCost), cash: '—', emphasis: false }
  ];
}

/**
 * Total first, then periods newest-first. A statement conventionally runs oldest to newest, but this is
 * a vertical list on a web page: eight quarters of scrolling to reach the one in progress is the wrong
 * default.
 */
export function orderedPeriods(report: ProfitLossReportDto): Array<{ period: ProfitLossPeriodDto; isTotal: boolean }> {
  const periods = [...report.periods].sort((a, b) => Date.parse(b.startUtc) - Date.parse(a.startUtc));
  return [{ period: report.total, isTotal: true }, ...periods.map(period => ({ period, isTotal: false }))];
}

/** No money in the range at all - worth saying, because a page of zeroes otherwise reads as broken. */
export function isEmptyReport(report: ProfitLossReportDto): boolean {
  const t = report.total.accrual;
  return t.revenue === 0 && t.costOfSales === 0 && t.operatingExpenses === 0;
}
